import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Folder,
  Calendar,
  Image as ImageIcon,
  Link as LinkIcon,
  Users,
  Sparkles,
  Plus,
  LifeBuoy,
  Lock,
  Building2,
  ChevronDown,
  Clapperboard,
  Aperture,
} from 'lucide-react';
import { useWorkspace } from '../context/WorkspaceContext';
import { useFilm } from '../context/FilmContext';
import { useFeatures } from '../context/FeatureContext';
import { useScopedNav } from '../hooks/useScopedNav';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { cn } from '../lib/utils';

/**
 * Sidebar — used in two variants:
 *   - "desktop": fixed left rail. < md hidden. md→lg: w-16 icon rail. lg+: w-64 full.
 *   - "mobile": drawer content (always full width inside Sheet).
 */
const Sidebar = ({ variant = 'desktop', onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { workspaces, currentWorkspace, selectWorkspace } = useWorkspace();
  const { currentFilm } = useFilm();
  const { features } = useFeatures() || {};
  const scoped = useScopedNav();

  const isMobile = variant === 'mobile';

  const wsId = currentWorkspace?.id || workspaces?.[0]?.id || null;

  // Plan tier badge — same rollup rule as DirectorsCommandView (any non-free
  // plan reads as PRO). When no workspace is selected we hide the badge.
  const isFreePlan = currentWorkspace
    ? (currentWorkspace.plan_id || 'plan_free') === 'plan_free'
    : null;

  // AI insights gating reads straight from FeatureContext — Task B migrated
  // that context to workspace-scoped /api/entitlements/workspace/{id}, so
  // `features` is already the current workspace's features (no separate
  // fetch needed). While it's still loading we fall back to the plan tier
  // (free workspaces are locked).
  const aiInsightsAllowed = useMemo(() => {
    if (features?.ai_insights === true) return true;
    const pid = currentWorkspace?.plan_id || 'plan_free';
    return pid !== 'plan_free';
  }, [features, currentWorkspace?.plan_id]);

  // Active-route matchers that work on ALL scoped URL shapes:
  //   /<key>, /workspaces/W/<key>, /workspaces/W/films/F/<key>, legacy /films/F/<key>
  const isActive = (key) =>
    new RegExp(`(?:^|/)${key}(?:$|/|\\?)`).test(location.pathname);

  const navItems = useMemo(
    () => [
      {
        key: 'home',
        label: 'Home',
        icon: Home,
        to: '/',
        active: location.pathname === '/',
      },
      {
        key: 'projects',
        label: 'Projects',
        icon: Folder,
        to: scoped.to.projects(),
        active:
          /^\/workspaces(\/|$)/.test(location.pathname) ||
          /^\/films(\/|$)/.test(location.pathname),
      },
      {
        key: 'calendar',
        label: 'Calendar',
        icon: Calendar,
        to: scoped.to.calendar(),
        active: isActive('calendar'),
      },
      {
        key: 'assets',
        label: 'Gear',
        icon: ImageIcon,
        to: scoped.to.gear(),
        active: isActive('gear') || isActive('assets'),
      },
      {
        key: 'files',
        label: 'Files',
        icon: LinkIcon,
        to: scoped.to.files(),
        active: isActive('files'),
      },
      {
        key: 'scenes',
        label: 'Scenes',
        icon: Clapperboard,
        to: scoped.to.scenes(),
        canAccess: scoped.canAccess.scenes,
        disabledReason: scoped.reason.scenes,
        active: isActive('scenes'),
      },
      {
        key: 'shots',
        label: 'Shots',
        icon: Aperture,
        to: scoped.to.shots(),
        canAccess: scoped.canAccess.shots,
        disabledReason: scoped.reason.shots,
        active: isActive('shots'),
      },
      {
        key: 'team',
        label: 'Team',
        icon: Users,
        to: scoped.to.team(),
        active:
          /(?:^|\/)team(?:$|\/|\?)/.test(location.pathname) ||
          location.pathname.endsWith('/members'),
      },
      {
        key: 'ai-insights',
        label: 'AI Insights',
        icon: Sparkles,
        to: '/ai-insights',
        active: location.pathname.startsWith('/ai-insights'),
        locked: !aiInsightsAllowed,
      },
    ],
    [location.pathname, scoped, aiInsightsAllowed]
  );

  const handleWorkspaceSelect = (ws) => {
    selectWorkspace(ws);
    navigate(`/workspaces/${ws.id}`);
    onNavigate?.();
  };

  // Phase 1 Task 4.3 — single-workspace users get no switcher affordance:
  // the identity block renders static (no dropdown, no chevron), consistent
  // with Home.jsx's auto-redirect for single-workspace users.
  const isSingleWorkspace = (workspaces?.length || 0) === 1;

  const workspaceIdentity = (
    <>
      <div className="h-8 w-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
        <Building2 className="h-4 w-4" />
      </div>
      <div
        className={cn(
          'flex-1 min-w-0 text-left',
          isMobile ? 'block' : 'hidden lg:block'
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-sm font-semibold font-display truncate leading-tight min-w-0">
            {currentWorkspace?.name || 'Select Workspace'}
          </p>
          {currentWorkspace && (
            isFreePlan ? (
              <span
                className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-md border border-border bg-card text-muted-foreground text-[9px] font-bold tracking-[0.22em] uppercase"
                data-testid="appshell-workspace-plan"
              >
                Free
              </span>
            ) : (
              <span
                className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-md bg-warning text-warning-foreground text-[9px] font-bold tracking-[0.22em] uppercase shadow-sm"
                data-testid="appshell-workspace-plan"
              >
                Pro
              </span>
            )
          )}
        </div>
        <p className="text-[11px] text-muted-foreground truncate">
          {currentWorkspace
            ? `${currentWorkspace.film_count || 0} films`
            : 'Choose workspace'}
        </p>
      </div>
    </>
  );

  const containerClass = isMobile
    ? 'flex flex-col h-full w-full bg-card text-card-foreground'
    : cn(
        'hidden md:flex md:flex-col md:sticky md:top-0 md:h-screen',
        'border-r border-border bg-card text-card-foreground',
        'md:w-16 lg:w-64 flex-shrink-0 transition-all'
      );

  // Show full content in mobile or in lg+ desktop
  return (
    <aside className={containerClass}>
      {/* Top: brand / workspace selector */}
      <div
        className={cn(
          'flex flex-col gap-2 border-b border-border',
          isMobile ? 'p-5' : 'px-3 py-4 lg:p-5'
        )}
      >
        {/* Eyebrow + workspace selector — full label hidden on collapsed rail */}
        <div className={cn('hidden', isMobile ? 'block' : 'lg:block')}>
          <p className="text-[10px] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
            Production Hub
          </p>
        </div>

        {isSingleWorkspace ? (
          <div
            className={cn(
              'flex items-center rounded-xl border border-border bg-background/60',
              isMobile ? 'gap-3 px-3 py-2.5' : 'gap-3 px-3 py-2.5 lg:px-3 lg:py-3',
              isMobile ? 'w-full' : 'w-full justify-center lg:justify-start'
            )}
            data-testid="appshell-workspace-static"
          >
            {workspaceIdentity}
          </div>
        ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                'group flex items-center rounded-xl border border-border bg-background/60 hover:bg-accent transition-colors',
                isMobile ? 'gap-3 px-3 py-2.5' : 'gap-3 px-3 py-2.5 lg:px-3 lg:py-3',
                isMobile ? 'w-full' : 'w-full justify-center lg:justify-start'
              )}
              data-testid="appshell-workspace-trigger"
            >
              {workspaceIdentity}
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-muted-foreground flex-shrink-0',
                  isMobile ? 'block' : 'hidden lg:block'
                )}
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            {workspaces?.length > 0 ? (
              workspaces.map((ws) => (
                <DropdownMenuItem
                  key={ws.id}
                  onClick={() => handleWorkspaceSelect(ws)}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{ws.name}</span>
                    <span className="text-[11px] text-muted-foreground">
                      {ws.film_count || 0} films · {ws.member_count || 1} members
                    </span>
                  </div>
                </DropdownMenuItem>
              ))
            ) : (
              <DropdownMenuItem disabled>No workspaces yet</DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer"
              onSelect={() => {
                navigate('/workspace/purchase');
                onNavigate?.();
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create New Workspace
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer"
              onSelect={() => {
                navigate('/workspaces');
                onNavigate?.();
              }}
            >
              <Building2 className="h-4 w-4 mr-2" />
              All Workspaces
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        )}

        {/* "Working in: <Film>" — subtle secondary label. Shown only when a film
            is selected via Phase 1 context; hidden on the collapsed icon rail. */}
        {currentFilm?.title && (
          <p
            className={cn(
              'text-[11px] text-muted-foreground truncate',
              isMobile ? 'block' : 'hidden lg:block'
            )}
            data-testid="sidebar-working-in"
            title={`Working in: ${currentFilm.title}`}
          >
            Working in:{' '}
            <span className="text-foreground font-medium">{currentFilm.title}</span>
          </p>
        )}
      </div>

      {/* Primary nav */}
      <nav className={cn('flex-1 overflow-y-auto', isMobile ? 'px-3 py-4' : 'px-2 py-4 lg:px-3')}>
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.key}>
              <NavLinkItem
                item={item}
                isMobile={isMobile}
                onNavigate={onNavigate}
              />
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer: account / support */}
      <div
        className={cn(
          'border-t border-border',
          isMobile ? 'p-4 space-y-3' : 'p-3 lg:p-4 space-y-2 lg:space-y-3'
        )}
      >
        <div
          className={cn(
            'flex items-center gap-2',
            isMobile ? 'justify-start' : 'justify-center lg:justify-start lg:gap-2'
          )}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/profile"
                onClick={onNavigate}
                className="h-9 w-9 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                aria-label="Account"
                data-testid="appshell-account"
              >
                <LifeBuoy className="h-4 w-4" />
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right">Account</TooltipContent>
          </Tooltip>
          <span
            className={cn(
              'text-[11px] tracking-wider text-muted-foreground uppercase ml-1',
              isMobile ? 'inline' : 'hidden lg:inline'
            )}
          >
            Account
          </span>
        </div>
      </div>
    </aside>
  );
};

const NavLinkItem = ({ item, isMobile, onNavigate }) => {
  const Icon = item.icon;

  // Phase 3 disabled state: film-only items (Scenes, Shots) when no film is
  // selected. We DO NOT render an <a> element — clicks must not fire and
  // assistive tech must announce the disabled state via aria-disabled.
  const disabled = item.canAccess === false || item.to == null;

  if (disabled) {
    const disabledContent = (
      <button
        type="button"
        disabled
        aria-disabled="true"
        data-testid={`sidebar-nav-${item.key}`}
        data-disabled="true"
        className={cn(
          'group relative flex items-center rounded-xl text-sm font-medium',
          'cursor-not-allowed select-none',
          isMobile
            ? 'gap-3 px-3 py-2.5 w-full'
            : 'gap-3 px-3 py-2.5 w-full md:justify-center lg:justify-start',
          'text-muted-foreground/40'
        )}
      >
        <Icon className="h-[18px] w-[18px] flex-shrink-0 opacity-60" aria-hidden />
        <span
          className={cn(
            'flex-1 tracking-wide uppercase text-[12px] text-left',
            isMobile ? 'inline' : 'hidden lg:inline'
          )}
        >
          {item.label}
        </span>
        <Lock
          className={cn(
            'h-3 w-3 text-muted-foreground/40 flex-shrink-0',
            isMobile ? 'inline-block' : 'hidden lg:inline-block'
          )}
          aria-hidden
        />
      </button>
    );
    return (
      <Tooltip>
        <TooltipTrigger asChild>{disabledContent}</TooltipTrigger>
        <TooltipContent side="right">
          Select a film to access {item.label}.
        </TooltipContent>
      </Tooltip>
    );
  }

  const content = (
    <Link
      to={item.to}
      onClick={onNavigate}
      data-testid={`sidebar-nav-${item.key}`}
      className={cn(
        'group relative flex items-center rounded-xl text-sm font-medium transition-colors',
        isMobile
          ? 'gap-3 px-3 py-2.5 w-full'
          : 'gap-3 px-3 py-2.5 w-full md:justify-center lg:justify-start',
        item.active
          ? 'bg-primary/12 text-primary'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground'
      )}
    >
      {item.active && (
        <span className="absolute left-0 top-2 bottom-2 w-0.5 rounded-r bg-primary" aria-hidden />
      )}
      <Icon className="h-[18px] w-[18px] flex-shrink-0" />
      <span
        className={cn(
          'flex-1 tracking-wide uppercase text-[12px]',
          isMobile ? 'inline' : 'hidden lg:inline'
        )}
      >
        {item.label}
      </span>
      {item.locked && (
        <Lock
          className={cn(
            'h-3.5 w-3.5 text-muted-foreground/70 flex-shrink-0',
            isMobile ? 'inline-block' : 'hidden lg:inline-block'
          )}
        />
      )}
    </Link>
  );

  if (isMobile) return content;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent side="right" className="lg:hidden">
        {item.label}
        {item.locked ? ' · Pro' : ''}
      </TooltipContent>
    </Tooltip>
  );
};

export default Sidebar;
